import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { TextArea } from '@/components/TextArea';
import { TextInput } from '@/components/TextInput';
import { useEventStore } from '@/stores/useEventStore';
import { formatDateTime } from '@/utils/formatDateTime';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

type SendMeetingRequestModalProps = {
  userId: number | null;
  isOpen: boolean;
  onClose: () => void;
};
export const SendMeetingRequestModal = ({
  isOpen,
  userId,
  onClose,
}: SendMeetingRequestModalProps) => {
  const { t } = useTranslation('eventDetails');
  const { event, sendMeetingRequest } = useEventStore();
  type FormValues = {
    date: string;
    message: string;
  };

  const initialValues: FormValues = {
    date: event?.date || '',
    message: '',
  };
  if (!event) return null;

  const validationSchema = Yup.object().shape({
    date: Yup.date()
      .nullable()
      .required(t('common:validation.required'))
      .min(
        event.date,
        t('common:validation.date.min', {
          min: formatDateTime(event.date),
        }),
      )
      .test(
        'is-before-end-date',
        t('common:validation.date.max', {
          max: event.end_date ? formatDateTime(event.end_date) : '',
        }),
        function (value) {
          if (!event.end_date || !value) return true; // Pass if no end_date or no value
          return new Date(value) <= new Date(event.end_date);
        },
      ),
    message: Yup.string().required(t('common:validation.required')),
  });

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, setErrors }: FormikHelpers<FormValues>,
  ) => {
    if (!userId) return;
    try {
      await sendMeetingRequest(event.id, userId, values.date, values.message);
      onClose();
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('sendMeetingRequest.title')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <>
          <Text className="mb-4 text-gray-500">
            {t('sendMeetingRequest.description')}
          </Text>
          <Form className="space-y-4">
            <TextInput
              containerStyle="w-full"
              label={t('sendMeetingRequest.date')}
              name="date"
              max={event.end_date}
              min={event.date}
              type="datetime-local"
            />
            <TextArea
              label={t('sendMeetingRequest.message')}
              name="message"
              rows={4}
            />
            <Button type="submit">{t('common:send')}</Button>
          </Form>
        </>
      </Formik>
    </Modal>
  );
};
